// Components
import { Select, Textarea, Stack, Skeleton } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormActions } from "@benchmark/ui/components/atomic";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum } from "@medplum/react";
import { useCallback, useEffect } from "react";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";

// Types
import type { Goal, Patient } from "@medplum/fhirtypes";
import type { GoalProgressFormData } from "@benchmark/core/types";

// Utils
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { createGoalFacade } from "@/facades/goal.facade";
import {
  formatDateTimeForInput,
  formatInputForDateTime,
} from "@benchmark/ui/utils";
import { GAS_SCORE_SELECT_OPTIONS } from "@benchmark/core/constants";

/**
 * Props for the GoalProgressForm component.
 * @agentic connections
 */
export type GoalProgressFormProps = {
  goal: Goal;
  patientId: string;
  onSuccess: () => void;
  onCancel: () => void;
  /** When true, renders as nested form fields without form wrapper or submit button */
  nested?: boolean;
  /** Callback to sync form data to parent (used when nested) */
  onFormDataChange?: (formData: GoalProgressFormData) => void;
};

/**
 * Form for adding progress updates to goals using GAS (Goal Attainment Scale) scoring.
 * @agentic connections
 */
export const GoalProgressForm = ({
  goal,
  patientId,
  onSuccess,
  onCancel,
  nested = false,
  onFormDataChange,
}: GoalProgressFormProps) => {
  const medplum = useMedplum();
  const goalFacade = createGoalFacade(medplum);
  const { data: patientResource } = usePatientResource(patientId);
  const patient = patientResource as Patient | undefined;
  const { practitionerRole } = useCurrentPractitionerRole();

  const form = useForm<GoalProgressFormData>({
    initialValues: {
      goalId: goal.id || "",
      gasScore: 0,
      effectiveDateTime: new Date().toISOString(), // Will be updated when user selects date/time
      note: "",
    },
    validate: {
      goalId: (value) => (!value ? "Goal ID is required" : null),
      gasScore: (value) => {
        if (value === undefined || value === null) {
          return "GAS score is required";
        }
        if (value < -2 || value > 2) {
          return "GAS score must be between -2 and +2";
        }
        return null;
      },
      effectiveDateTime: (value) => (!value ? "Date is required" : null),
    },
  });

  const { markAsClean, ConfirmationModal } = useFormProtection(form, {
    enabled: !nested,
    blockNavigation: !nested,
    confirmationMessage:
      "You have unsaved progress changes. Are you sure you want to close?",
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  // Sync form data to parent when nested mode is enabled
  useEffect(() => {
    if (nested && onFormDataChange) {
      onFormDataChange(form.values);
    }
  }, [nested, onFormDataChange, form.values]);

  const handleSubmit = useCallback(
    (formData: GoalProgressFormData) => {
      return createSubmissionHandler(async (fd: GoalProgressFormData) => {
        if (!patient || !practitionerRole) {
          showErrorNotification("Missing patient or practitioner information.");
          return;
        }

        if (!goal.id) {
          showErrorNotification("Goal must have an ID.");
          return;
        }

        try {
          await goalFacade.recordProgress(fd, patient, practitionerRole);
          showSuccessNotification("Progress recorded successfully.");
          markAsClean();
          onSuccess();
        } catch (error) {
          showErrorNotification(error, {
            title: "Unable to Record Progress",
          });
        }
      })(formData);
    },
    [
      patient,
      practitionerRole,
      goalFacade,
      goal,
      onSuccess,
      createSubmissionHandler,
      markAsClean,
    ],
  );

  if (!patient || !practitionerRole) {
    return <Skeleton height={200} />;
  }

  // Prepare GAS score options with descriptions
  const gasScoreOptions = GAS_SCORE_SELECT_OPTIONS.map((option) => ({
    value: String(option.value),
    label: `${option.value >= 0 ? "+" : ""}${option.value} - ${option.label}`,
    description: option.description,
  }));

  const formFields = (
    <Stack gap="md">
      <FormField label="Date" required aria-label="Progress date">
        <input
          type="datetime-local"
          value={
            form.values.effectiveDateTime
              ? formatDateTimeForInput(form.values.effectiveDateTime)
              : ""
          }
          onChange={(event) => {
            const dateValue = event.target.value;
            if (dateValue) {
              // Convert datetime-local input to ISO string (UTC) for FHIR storage
              form.setFieldValue(
                "effectiveDateTime",
                formatInputForDateTime(dateValue),
              );
            }
          }}
          aria-label="Progress date"
          style={{
            padding: "var(--mantine-spacing-xs)",
            border: "1px solid var(--mantine-color-gray-4)",
            borderRadius: "var(--mantine-radius-sm)",
          }}
        />
      </FormField>

      <FormField
        label="Scoring System"
        required
        description="Goal Attainment Scale (GAS) score for this progress update"
        aria-label="GAS score"
      >
        <Select
          placeholder="Select GAS score"
          data={gasScoreOptions}
          value={String(form.values.gasScore)}
          onChange={(value) => {
            if (value) {
              form.setFieldValue("gasScore", Number.parseInt(value, 10));
            }
          }}
          searchable={false}
          clearable={false}
          aria-label="GAS score"
        />
      </FormField>

      <FormField label="Notes" aria-label="Progress notes">
        <Textarea
          placeholder="Add any notes about this progress update..."
          value={form.values.note || ""}
          onChange={(event) => {
            form.setFieldValue("note", event.currentTarget.value);
          }}
          minRows={3}
          aria-label="Progress notes"
        />
      </FormField>

      {!nested && (
        <FormActions
          isSubmitting={isSubmitting}
          resourceType="Progress Update"
          isEditing={false}
          showCancel={true}
          onCancel={onCancel}
        />
      )}
    </Stack>
  );

  // When nested, render fields without form wrapper
  if (nested) {
    return formFields;
  }

  // When standalone, render with form wrapper and submit handler
  return (
    <>
      <form
        aria-label="Add goal progress form"
        onSubmit={form.onSubmit(handleSubmit, (_validationErrors) => {
          showErrorNotification(
            "Please fix the form errors before submitting.",
          );
        })}
      >
        {formFields}
      </form>
      <ConfirmationModal />
    </>
  );
};
